import React from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';

import { Review } from 'utils/types';
import config from 'config';

/**
 * A HOC for retrieving a single hotel
 *
 * @param {React.Component|function} Component A react component to wrap
 *
 * @returns {React.Component|*} Returns the passed in component with new props
 */
export const withHotelReviews = Component => {
    // eslint-disable-next-line react/prefer-stateless-function
    class Container extends React.Component {
        static displayName = `withHotelReviews(${Component.displayName || Component.name})`;
        static propTypes = {
            hotelId: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]).isRequired,
            first: PropTypes.number.isRequired,
            filter: PropTypes.string,
            redirected: PropTypes.bool,
            dataReviews: PropTypes.arrayOf(PropTypes.object)
        };

        state = {
            page: 1,
            hasMoreReviews: this.props.dataReviews && this.props.dataReviews.length === this.props.first,
            reviews: this.props.dataReviews ? this.props.dataReviews.map(dataReview => new Review(dataReview.data)) : []
        };

        componentDidMount() {
            const { redirected } = this.props;
            if (redirected) {
                this.fetchReviews();
            }
        }

        updateReviews = (newReviews, hasMoreReviews) => {
            this.setState({
                hasMoreReviews,
                reviews: [
                    ...this.state.reviews,
                    ...newReviews
                ],
                page: this.state.page + 1
            });
        };

        fetchReviews = () => {
            const { hotelId, first, filter } = this.props;
            if (hotelId) {
                axios({
                    url: `${config.ROOT_DIR}hotelReviews.json`,
                    method: 'get',
                    params: {
                        variables: {
                            hotelId,
                            first,
                            offset: first * this.state.page,
                            travelerTypeId: filter
                        }
                    }
                })
                    .then(res => res.data)
                    .then(data => {
                        const reviews =
                            data && data.hotel && data.hotel.reviews && data.hotel.reviews.edges
                                ? data.hotel.reviews.edges.map(edge => new Review(edge.node))
                                : [];
                        const hasMoreReviews =
                            data && data.hotel && data.hotel.reviews && data.hotel.reviews.pageInfo
                                ? data.hotel.reviews.pageInfo.hasNextPage
                                : false;
                        this.updateReviews(reviews, hasMoreReviews);
                    })
                    .catch(() => {
                        this.updateReviews([], false);
                    });
            }
        };

        render = () => <Component {...this.props} {...this.state} fetchReviews={this.fetchReviews} />;
    }

    return Container;
};

/**
 * A render-props version of the HOC to retrieve a single hotel
 *
 * @type {React.Component}
 */
const WithHotelReviews = withHotelReviews(({ render, reviews, hasMoreReviews, fetchReviews }) =>
    render(reviews, hasMoreReviews, fetchReviews)
);

export default WithHotelReviews;
